import {Button, Popover, Stack, Typography, Chip, Box} from "@mui/material";
import {useRef, useState} from "react";
import {MaterialSymbol} from "react-material-symbols";
import {BORDER_RADIUS_INNER} from "../../../../../config/themes/theme";
import FullScreenOverlay from "../../../../../components/full-screen-overlay";
import {useGetExamConfigQuery} from "../../../../../store/api/examConfigApiSlice";
import {useSearchParams} from "react-router-dom";
import {FilterType} from "../../../../../config/enums";
import {STYLES} from "./STYLES";

export default function FilterButtonUI() {
  const [open, setOpen] = useState(false);
  const anchorEl = useRef<HTMLButtonElement>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const attemptStatusParams = parseQueryParam(
    searchParams.get(FilterType.ATTEMPT_STATUS),
  );
  const patternsParams = parseQueryParam(searchParams.get(FilterType.PATTERN));
  const difficultyParams = parseQueryParam(
    searchParams.get(FilterType.DIFFICULTY),
  );

  const [selectedDifficulty, setSelectedDifficulty] = useState<string[]>([]);
  const [selectedPatterns, setSelectedPatterns] = useState<string[]>([]);
  const [selectedAttemptStatus, setSelectedAttemptStatus] = useState<string[]>(
    [],
  );

  const {currentData} = useGetExamConfigQuery();
  const difficultyList = currentData?.difficulty;
  const patternList = currentData?.patterns;
  const attemptStatusList = currentData?.attemptStatus;

  // Initialize selected filters when dropdown opens
  const handleOpenFilters = () => {
    setSelectedDifficulty(difficultyParams);
    setSelectedPatterns(patternsParams);
    setSelectedAttemptStatus(attemptStatusParams);
    setOpen(true);
  };

  const handleApply = () => {
    if (selectedDifficulty.length > 0) {
      searchParams.set(FilterType.DIFFICULTY, selectedDifficulty.join(","));
    } else {
      searchParams.delete(FilterType.DIFFICULTY);
    }

    if (selectedPatterns.length > 0) {
      searchParams.set(FilterType.PATTERN, selectedPatterns.join(","));
    } else {
      searchParams.delete(FilterType.PATTERN);
    }

    if (selectedAttemptStatus.length > 0) {
      searchParams.set(
        FilterType.ATTEMPT_STATUS,
        selectedAttemptStatus.join(","),
      );
    } else {
      searchParams.delete(FilterType.ATTEMPT_STATUS);
    }

    setSearchParams(searchParams);
    setOpen(false);
  };

  const handleClear = () => {
    setSelectedDifficulty([]);
    setSelectedPatterns([]);
    setSelectedAttemptStatus([]);
  };

  const toggleDifficulty = (id: string) => {
    setSelectedDifficulty(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id],
    );
  };

  const togglePattern = (id: string) => {
    setSelectedPatterns(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id],
    );
  };

  const toggleAttemptStatus = (id: string) => {
    setSelectedAttemptStatus(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id],
    );
  };

  return (
    <>
      <Button
        ref={anchorEl}
        variant="contained"
        color="primary"
        startIcon={
          <MaterialSymbol
            icon="page_info"
            size={16}
            weight={400}
            fill
            style={{marginLeft: "2px"}}
          />
        }
        onClick={handleOpenFilters}
        sx={STYLES.filterButton}>
        Filters
      </Button>
      <Popover
        open={open}
        onClose={() => {
          handleApply();
          setOpen(false);
        }}
        anchorEl={anchorEl.current}
        anchorOrigin={STYLES.popoverAnchorOrigin}
        transformOrigin={STYLES.popoverTransformOrigin}
        slotProps={{
          root: {sx: STYLES.popoverRootSlotProps},
          paper: {sx: STYLES.popoverPaperSlotProps},
        }}>
        <Stack>
          <Stack gap={1} m={2.5}>
            <Typography fontWeight={500} fontSize={"0.875rem"}>
              DIFFICULTY
            </Typography>
            <Stack direction="row" spacing={1}>
              {difficultyList?.map(difficulty => (
                <FilterChip
                  key={difficulty.id}
                  label={difficulty.viewModel.name}
                  isSelected={selectedDifficulty.includes(difficulty.id)}
                  onClick={() => toggleDifficulty(difficulty.id)}
                />
              ))}
            </Stack>
            <Box height={4} />
            <Typography fontWeight={500} fontSize={"0.875rem"}>
              PATTERNS
            </Typography>
            <Stack direction="row" spacing={1}>
              {patternList?.map(pattern => (
                <FilterChip
                  key={pattern.id}
                  label={pattern.viewModel.name}
                  isSelected={selectedPatterns.includes(pattern.id)}
                  onClick={() => togglePattern(pattern.id)}
                />
              ))}
            </Stack>
            <Box height={4} />
            <Typography fontWeight={500} fontSize={"0.875rem"}>
              STATUS
            </Typography>
            <Stack direction="row" spacing={1}>
              {attemptStatusList?.map(attemptStatus => (
                <FilterChip
                  key={attemptStatus.id}
                  label={attemptStatus.viewModel.name}
                  isSelected={selectedAttemptStatus.includes(attemptStatus.id)}
                  onClick={() => toggleAttemptStatus(attemptStatus.id)}
                />
              ))}
            </Stack>
          </Stack>
          <Stack direction="row" sx={STYLES.popoverFooter}>
            <Button variant="text" color="secondary" onClick={handleClear}>
              Clear
            </Button>
            <Button variant="contained" color="primary" onClick={handleApply}>
              Apply
            </Button>
          </Stack>
        </Stack>
      </Popover>
      {open && <FullScreenOverlay open={open} onClick={() => setOpen(false)} />}
    </>
  );
}

function FilterChip({
  label,
  isSelected,
  onClick,
}: {
  label: string;
  isSelected: boolean;
  onClick?: () => void;
}) {
  return (
    <Chip
      label={label}
      onClick={onClick}
      sx={{
        backgroundColor: isSelected ? "secondary.dark" : "secondary.main",
        color: isSelected ? "white" : "primary.contrastText",
        borderRadius: BORDER_RADIUS_INNER,
        cursor: "pointer",
        "&:hover": {
          backgroundColor: isSelected ? "secondary.dark" : "secondary.main",
          color: isSelected ? "white" : "primary.contrastText",
        },
      }}
    />
  );
}

const parseQueryParam = (param: string | null) => {
  if (!param) return [];
  return param.split(",");
};
